import { useGetMyExaminationTimeQuery } from '@exam-room/graphql';
import { useEffect, useMemo, useState } from 'react';

import { useNetwork } from '../network';

interface ExaminationTime {
  order: number;
  extendedDurationInMs: number;
  postponedDurationInMs: number;
  totalManualExtendedDurationInMs: number;
  current: boolean;
}

type MyExaminationTime = ExaminationTime[];

interface UseExaminationTimeProps {
  examCardId: string | undefined;
  skip?: boolean;
}

interface UseExaminationTimeReturn {
  examinationTime: MyExaminationTime | null;
  currentExaminationTime: ExaminationTime | null;
  loading: boolean;
  error: Error | undefined;
}

export function useExaminationTime({
  examCardId,
  skip = false,
}: Readonly<UseExaminationTimeProps>): UseExaminationTimeReturn {
  const { isOnline } = useNetwork();

  const {
    data: timeData,
    loading,
    error,
  } = useGetMyExaminationTimeQuery({
    variables: {
      examCardId: examCardId ?? '',
    },
    skip: !examCardId || skip || !isOnline,
    pollInterval: 60000,
  });

  const liveExaminationTime = useMemo<MyExaminationTime | null>(() => {
    if (!timeData?.erGetMyExaminationTime) return null;

    return timeData.erGetMyExaminationTime.examinationTime.map((timeData) => ({
      order: timeData.round,
      extendedDurationInMs: timeData.extraDurationInMs,
      postponedDurationInMs: timeData.postponedDurationInMs,
      totalManualExtendedDurationInMs: timeData.totalManualExtendedDurationInMs,
      current: timeData.current,
    }));
  }, [timeData]);

  const [cachedExaminationTime, setCachedExaminationTime] = useState<MyExaminationTime | null>(
    null,
  );

  useEffect(() => {
    if (liveExaminationTime) {
      setCachedExaminationTime(liveExaminationTime);
    }
  }, [liveExaminationTime]);

  const currentExaminationTime = useMemo(
    () => cachedExaminationTime?.find((examTime) => examTime.current === true) ?? null,
    [cachedExaminationTime],
  );

  return {
    examinationTime: liveExaminationTime ?? cachedExaminationTime,
    currentExaminationTime,
    loading,
    error,
  };
}
